import { Box, Flex, NextProvider } from '@hover/blueprint';
import React, { useReducer } from 'react';
import type { RouteProps } from 'react-router';
import { Route, Switch } from 'react-router';

import { CheckEmail } from 'src/modules/onboarding/components/CheckEmail';
import { Login } from 'src/modules/onboarding/components/Login';
import {
  OnboardingContext,
  onboardingFormReducer,
} from 'src/modules/onboarding/components/OnboardingContext';
import { Password } from 'src/modules/onboarding/components/Password';
import { ProgressWrapper } from 'src/modules/onboarding/components/ProgressWrapper';
import { ResetPassword } from 'src/modules/onboarding/components/ResetPassword';
import { SSOFailure } from 'src/modules/onboarding/components/SSOFailure';
import { SSOSuccess } from 'src/modules/onboarding/components/SSOSuccess';
import { Signup } from 'src/modules/onboarding/components/Signup';
import { UserCompanyName } from 'src/modules/onboarding/components/UserCompanyName';
import { UserName } from 'src/modules/onboarding/components/UserName';
import { UserOrgDescription } from 'src/modules/onboarding/components/UserOrgDescription';
import { UserOrgType } from 'src/modules/onboarding/components/UserOrgType';
import { UserPersona } from 'src/modules/onboarding/components/UserPersona';
import { Welcome } from 'src/modules/onboarding/components/Welcome';
import { useIsMobileSize } from 'src/modules/renderings/hooks';
import { ExternalRedirect } from 'src/shared/components';
import { StyledNavBar } from 'src/shared/components/NavBar';
import { useDetectUserSessionQuery } from 'src/shared/graphql/generated';
import { baseUrlToRedirectUrl } from 'src/shared/helpers/url';
import { useLocation } from 'src/shared/hooks';
import { BrandProvider } from 'src/shared/providers/Brand';
import { Route as Routes } from 'src/shared/route';

import { DownloadApp } from './DownloadApp';
import { UserInvite } from './UserInvite';
import { UserOrgUsage } from './UserOrgUsage';
import { UserPhone } from './UserPhone';

const ProgressRoute = ({
  children,
  ...routeProps
}: React.PropsWithChildren<RouteProps>) => (
  <Route {...routeProps}>
    <ProgressWrapper>{children}</ProgressWrapper>
  </Route>
);

export const Router = () => {
  const reducerTuple = useReducer(onboardingFormReducer, {
    orgDescriptions: [],
  });

  const { href } = useLocation();

  const isMobile = useIsMobileSize();

  const { data } = useDetectUserSessionQuery({
    fetchPolicy: 'network-only',
  });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const isPartialProfile = data && !data?.profile?.user?.orgs?.[0]?.id;

  if (data && !isPartialProfile && !href.includes('/ssoSuccess')) {
    return <ExternalRedirect url={baseUrlToRedirectUrl(href)} />;
  }

  return (
    <Switch>
      <Route path={Routes.SIGN_UP_ONBOARDING.path}>
        <NextProvider attach>
          <Flex flexDir="column" height="100vh">
            <BrandProvider>
              {!isMobile && (
                // I think this should ideally be in the shared component, but as it is now only the
                // onboarding navbar has a separating line.
                <Box
                  borderBottomColor="neutral.200"
                  borderBottomStyle="solid"
                  borderBottomWidth="400"
                  width={1}
                >
                  <StyledNavBar
                    redirectPath={Routes.ROOT.path}
                    useDefaultLogo
                  />
                </Box>
              )}
              <OnboardingContext.Provider value={reducerTuple}>
                <Route exact path={`${Routes.SIGN_UP_ONBOARDING.path}/create`}>
                  <Welcome />
                </Route>
                <Route exact path={`${Routes.SIGN_UP_ONBOARDING.path}/welcome`}>
                  <Welcome isLogin />
                </Route>
                <Route exact path={`${Routes.SIGN_UP_ONBOARDING.path}/signup`}>
                  <Signup />
                </Route>
                <Route
                  exact
                  path={[
                    `${Routes.SIGN_UP_ONBOARDING.path}/login`,
                    `${Routes.SIGN_UP_ONBOARDING.path}/login/sso`,
                  ]}
                >
                  <Login />
                </Route>
                <Route
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/ssoSuccess`}
                >
                  <SSOSuccess />
                </Route>
                <Route
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/ssoFailure`}
                >
                  <SSOFailure />
                </Route>
                <Route exact path={`${Routes.SIGN_UP_ONBOARDING.path}/check`}>
                  <CheckEmail />
                </Route>
                <Route
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/password`}
                >
                  <Password />
                </Route>
                <Route exact path={`${Routes.SIGN_UP_ONBOARDING.path}/reset`}>
                  <ResetPassword />
                </Route>
                <ProgressRoute
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/name`}
                >
                  <UserName />
                </ProgressRoute>
                <ProgressRoute
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/persona`}
                >
                  <UserPersona />
                </ProgressRoute>
                <ProgressRoute
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/type`}
                >
                  <UserOrgType />
                </ProgressRoute>
                <ProgressRoute
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/company`}
                >
                  <UserCompanyName />
                </ProgressRoute>
                <ProgressRoute
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/description`}
                >
                  <UserOrgDescription />
                </ProgressRoute>
                <ProgressRoute
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/usage`}
                >
                  <UserOrgUsage />
                </ProgressRoute>
                <ProgressRoute
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/invite`}
                >
                  <UserInvite />
                </ProgressRoute>
                <ProgressRoute
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/phone`}
                >
                  <UserPhone />
                </ProgressRoute>
                <ProgressRoute
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/download`}
                >
                  <DownloadApp />
                </ProgressRoute>
              </OnboardingContext.Provider>
            </BrandProvider>
          </Flex>
        </NextProvider>
      </Route>
    </Switch>
  );
};
